import { useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { AlertTitle, Box, VStack } from '@chakra-ui/react';

import { WarningTwoIcon } from '@chakra-ui/icons';

import { Alert, AlertDescription, Icon } from '@/components/v4';
import ChakraLink from 'components/v4/Link';
import ScopeType from '@/utils/enums/ScopeType';
import getRuntimeAppConfig from '@/utils/runtimeAppConfig';
import {
    WHOLESALER_BANNER_HEIGHT_VAR,
    alertStyles,
} from '@/utils/layoutCssVars';

const DEFAULT_MESSAGE =
    'Your account is in late payment status. Please settle your outstanding balance to avoid service disruption.';
const TITLE = 'Action required: account payment overdue.';

const WholesalerPaymentBanner = () => {
    const bannerRef = useRef(null);
    const { requiredScope, wholesalerPaymentBanner } = useSelector(
        (state) => state.login,
    );

    const isWholesalerAdmin = requiredScope === ScopeType.WHOLESALER;
    const isBannerEnabled = Boolean(wholesalerPaymentBanner?.enabled);
    const shouldRenderBanner = isWholesalerAdmin && isBannerEnabled;

    // (3101): keep the layout's reserved space in sync with this banner's
    // real height, so a longer message can't end up covering page content.
    useEffect(() => {
        if (!shouldRenderBanner) {
            document.documentElement.style.removeProperty(
                WHOLESALER_BANNER_HEIGHT_VAR,
            );
            return undefined;
        }
        const node = bannerRef.current;
        if (!node) {
            return undefined;
        }

        const setHeightVar = () => {
            document.documentElement.style.setProperty(
                WHOLESALER_BANNER_HEIGHT_VAR,
                `${node.offsetHeight}px`,
            );
        };
        setHeightVar();

        const observer = new ResizeObserver(setHeightVar);
        observer.observe(node);
        return () => observer.disconnect();
    }, [shouldRenderBanner]);

    if (!shouldRenderBanner) {
        return null;
    }

    const wholesalerPaymentBannerConfig =
        getRuntimeAppConfig()?.wholesalerPaymentBanner ?? {};
    const {
        status = 'error',
        title = TITLE,
        message = DEFAULT_MESSAGE,
        contactEmail,
    } = wholesalerPaymentBannerConfig;

    return (
        <Box ref={bannerRef}>
            <Alert
                status={status}
                variant="left-accent"
                mb={2}
                sx={alertStyles}>
                <Icon as={WarningTwoIcon} mr={2} boxSize={6} color="white" />
                <VStack align="start" spacing={0} flex="1" fontSize="md">
                    {title && <AlertTitle>{title}</AlertTitle>}
                    <AlertDescription flex={1}>
                        {message}
                        {contactEmail && (
                            <span>
                                {' '}
                                Please contact{' '}
                                <ChakraLink
                                    fontWeight="bold"
                                    textDecoration="none"
                                    _hover={{
                                        textDecoration: 'none',
                                    }}
                                    _active={{
                                        textDecoration: 'none',
                                    }}
                                    href={`mailto:${contactEmail}`}>
                                    {contactEmail}
                                </ChakraLink>{' '}
                                to resolve this.
                            </span>
                        )}
                    </AlertDescription>
                </VStack>
            </Alert>
        </Box>
    );
};

export default WholesalerPaymentBanner;
